import UsersRoles from "@/constants/users/UsersRoles";
import store from "@/stores/store.js";
import { SET } from "@/stores/CommumActionTypes.js";
import { UPDATE_USER_IMG_URL } from "@/stores/user/UserActionTypes";
import Controllers from "@/controllers";
import HTTPStatus from "@/constants/http-status/HTTPStatus";
// #region Factories Methods
/**
 * @summary Função que cria um objeto de usuário vazio.
 * @returns Objeto do Usuário.
 */
const EmptyUserViewFactory = function()
{
	return {
		id:          0,
		name:        "",
		email:       "",
		companiesId: 0,
		role:        "",
		claims:      []
	};
};

/**
 * @summary Cria o state de Usuário.
 * @returns {object} State de Usuário.
 */
const EmptyUserStateFactory = function()
{
	return {
		user:          EmptyUserViewFactory(),
		users:         [],
		profileImgUrl: `${process.env.VUE_APP_API_USER}nouser/picture/`,
	};
};

/**
 * @summary Cria a View do usuário a partir de um usuário enviado.
 * @param user View do usuário com as propriedades de usuário que precisam atualizar.
 * @returns Retorna um novo usuário com as propriedades atualizadas.
 */
const UserViewFactory = function(user = EmptyUserViewFactory())
{
	return {
		id:          user.id,
		name:        user.name,
		email:       user.email,
		companiesId: user.companiesId,
		role:        user.role,
		claims:      user.userClaims,
	};
};

/**
 * @summary Cria o model vazio do form para criação do usuário.
 * @returns Retorna o model para preencher o form de criação do usuário.
 */
const EmptyUserModelFactory = function()
{
	return {
		login:       "",
		name:        "",
		password:    "",
		companiesId: 0,
	};
};

/**
 * @summary Preenche um model de um usuário para criar ou atualizar.
 * Se não quiser alterar a senha do usuário durante a criação password deve ser passado como string vazia.
 * @param {string} userEmail - Email do usuário.
 * @param {string} userName - Nome completo do usuário.
 * @param {number} userCompaniesId  - Id da empresa do usuário.
 * @param {string} password - Senha do usuário.
 * @returns UserModel preenchido
 */
const UserModelFactory = function(userEmail = "", userName = "", userCompaniesId = 0, password = "")
{
	return {
		login:       userEmail,
		name:        userName,
		password:    password,
		companiesId: userCompaniesId,
	};
};

/**
 * @summary Cria o state de preferências do usuário.
 * @returns {object} State de Usuário.
 */
const EmptyUserPreferencesStateFactory = function()
{
	return {
		userPreferences: {
			theme:    "",
			language: "",
			timeZone: { name: "", offset: "" },
		},
	};
};

// #endregion

// #region Users Methods

/**
 * @summary Busca o usuário atual na base de dados
 * @returns {object} Usuário atual.
 */
const GetMe = async function()
{
	const resp = await Controllers.Users.GetMe();
	if (resp.status !== 200) return "";
	const user = UserViewFactory(resp.data);
	store.dispatch(`UserModule/${SET}`, user);
	store.dispatch(`UserModule/${UPDATE_USER_IMG_URL}`, user.id);
	return user;
};

/**
 * @summary Busca na base de dados todos os usuários existentes.
 * @returns {object} Retorna um objeto com todos os usuários da base de dados.
 */
const GetAllUsers = async function()
{
	const resp = await Controllers.Users.GetAllUsers();
	if (resp.status !== 200) return [];

	const users = resp.data.map((user) => UserViewFactory(user));
	return users;
};

/**
 * @summary Busca a url da imagem do profile do usuário.
 * @param {string} userId Id do usuário.
 * @returns {string} Url
 */
const GetUserProfileUrl = function(userId = "userId")
{
	const randonNumber = Math.floor(Math.random() * 100000);
	const url = `${process.env.VUE_APP_API_AQTECH}/Users/${userId}/picture/?${randonNumber}`;
	return url;
};

/**
 * @summary Busca o usuário criado na base de dados.
 * @param userModel Informações do usuário.
 * @returns {object} Usuário criado.
 */
const CreateUser = async function(userModel = EmptyUserModelFactory())
{
	const resp = await Controllers.Users.CreateUser(userModel);
	return UserViewFactory(resp.data);
};

/**
 * @summary Atualiza um usuário na base de dados.
 * @param {string} userId
 * @param {object} userModel Informações do novo usuário.
 * @returns {object} View do usuário atualizada.
 */
const UpdateUser = async function(userId, userModel)
{
	const resp = await Controllers.Users.UpdateUser(userId, userModel);
	return resp.data;
};

/**
 * @summary Busca o usuario atualizado na base de dados.
 * @param {string} userId Id do usuário.
 * @param {object} userProfileForm Informações gerais do usuário.
 * @returns {object} Usuário atualizado.
 */
const UpdateUserProfile = async function(userId, userProfileForm)
{
	const resp = await Controllers.Users.UpdateUserProfile(userId, userProfileForm);
	return resp.data;
};

/**
 * @summary Atualiza a imagem de perfil na base de dados.
 * @param {string} userId Id do usuário.
 * @param file Arquivo de imagem.
 */
const UpdateUserImage = async function(userId = "", file = File)
{
	await Controllers.Users.UpdateUserImage(userId, file);
};

/**
 * @summary Deleta um usuário na base de dados.
 * @param {string} userId Id do usuário.
 */
const DeleteUser = async function(userId)
{
	return await Controllers.Users.DeleteUser(userId);
};

/**
 * @summary Deleta a imagem do usuário na base de dados.
 * @param {string} userId Id do usuário.
 */
const DeleteUserProfileImage = async function(userId)
{
	await Controllers.Users.DeleteUserImage(userId);
};

/**
 * Obtém as preferências de um usuário do servidor.
 * @param {string} userId - O ID do usuário cujas preferências devem ser obtidas.
 * @returns {object} -  Objeto de preferências do usuário se bem-sucedido, caso contrário uma string vazia.
 */
const GetUserPreferences = async function(userId)
{
	const resp = await Controllers.UsersPreferences.GetUserPreferences(userId);
	if (resp.status === HTTPStatus.NO_CONTENT) return {};
	store.dispatch(`UserModule/UserPreferencesModule/${SET}`, resp.data);
	return resp.data;
};

/**
 * Atualiza as preferências de um usuário no servidor.
 * @param {string} userId - O ID do usuário cujas preferências devem ser atualizadas.
 * @param {object} preferences - O objeto de preferências contendo as preferências atualizadas.
 * @returns {object} - Objeto de preferências atualizadas se bem-sucedido, caso contrário uma string vazia.
 */
const PatchUserPreferences = async function(userId, preferences)
{
	const resp = await Controllers.UsersPreferences.PatchUserPreferences(userId, preferences);
	if (resp.status === HTTPStatus.NO_CONTENT) return {};
	return resp.data;
};

// #endregion

// #region Claims Methods

/**
 * @summary Busca a claim de usuário criada na base de dados.
 * @param {string} userId Id usuário.
 * @param {array} claimModels Array do objeto userClaimModel.
 * @returns {array} array das claims.
 */
const CreateUserClaim = async function(userId, claimModels)
{
	const resp = await Controllers.Users.CreateUserClaims(userId, claimModels);
	return resp.data;
};

/**
 * @summary Busca a claim do usuario atualizada na base de dados.
 * @param {string} userId Id do usuário.
 * @param {object} claimsModel Array de claims.
 * @returns {array} Claims atualizadas.
 */
const UpdateUserClaims = async function(userId, claimsModel)
{
	const resp = await Controllers.Users.UpdateUserClaims(userId, claimsModel);
	return resp.data;
};

// #endregion

// #region Roles Methods

/**
 * @summary Busca a role de usuário criada da base de dados.
 * @param {string} userId Id usuário.
 * @param {string} role nRole do usuário.
 * @returns {string} Role de usuário criada.
 */
const CreateUserRole = async function(userId, role)
{
	const resp = await Controllers.Users.CreateUserRole(userId, role);
	return resp.data;
};

/**
 * @summary Busca a role de usuário atualizada na base de dados.
 * @param {string} userId Id do usuário.
 * @param {string} newRole Nova role para atualizar na base.
 * @returns {string} Role atual do usuário.
 */
const UpdateUserRoles = async function(userId, newRole)
{
	const resp = await Controllers.Users.UpdateUserRoles(userId, newRole);
	return resp.data;
};

/**
 * @summary Verifica se a role é de Administrador Global
 * @param {string} role = role do usuário ex: GlobalAdministrator
 * @returns {bool} true se a role do usuário for GlobalAdministrator.
 */
const IsGlobalAdmin = function(role) { return role === UsersRoles.GLOBAL_ADM; };

/**
 * @summary Verifica se a role é de Administrador da Empresa.
 * @param {string} role = role do usuário ex: CompanyAdministrator
 * @returns {bool} true se a role do usuário for CompanyAdministrator.
 */
const IsCompanyAdmin = function(role) { return role === UsersRoles.COMPANY_ADM; };

/**
 * @summary Verifica se a role é de usuário DEMO.
 * @param {string} role = role do usuário ex: DEMO
 * @returns {bool} true se a role do usuário for usuário DEMO.
 */
const IsDemo = function(role) { return role === UsersRoles.DEMO; };

// #endregion

export default {
	CreateUser,
	CreateUserRole,
	CreateUserClaim,
	DeleteUser,
	DeleteUserProfileImage,
	EmptyUserModelFactory,
	EmptyUserStateFactory,
	EmptyUserViewFactory,
	EmptyUserPreferencesStateFactory,
	GetAllUsers,
	GetMe,
	GetUserProfileUrl,
	IsGlobalAdmin,
	IsCompanyAdmin,
	IsDemo,
	UpdateUser,
	UpdateUserProfile,
	UpdateUserRoles,
	UpdateUserImage,
	UpdateUserClaims,
	UserModelFactory,
	UserViewFactory,
	GetUserPreferences,
	PatchUserPreferences,
};
